import { useState, useEffect } from 'react';
import { api } from '../lib/api-client.js';

export interface UseCommanderThemesResult {
  themes: string[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches popular archetypes/themes for a commander from the server.
 * Returns up to 10 theme names. On error, returns an empty array.
 *
 * @param commanderName - The commander name to look up themes for.
 * @returns Object with themes, isLoading, and isError state.
 */
export function useCommanderThemes(commanderName: string): UseCommanderThemesResult {
  const [themes, setThemes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!commanderName) {
      setThemes([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    api
      .getCommanderThemes(commanderName)
      .then((response) => {
        if (!cancelled) {
          setThemes(response.themes);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error(`[useCommanderThemes] Failed to fetch themes for ${commanderName}:`, err);
        if (!cancelled) {
          setThemes([]);
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [commanderName]);

  return { themes, isLoading, isError };
}
